import { useMessageDraftIdentity } from "@/context/MessageInputContext";
import {
  isEditorStateEmpty,
  readMessageDraft,
  removeMessageDraft,
  resolveDraftStorageKey,
  writeMessageDraft,
} from "@/utils/messageDraftStorage";
import type { ReferencedEntity } from "@cohesive/api-client";
import { ConnectionNode } from "@cohesive/ui-components";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getRoot,
  $isElementNode,
  CLEAR_EDITOR_COMMAND,
  type LexicalEditor,
  type LexicalNode,
  type SerializedEditorState,
} from "lexical";
import { useCallback, useEffect, useRef } from "react";

const SAVE_DEBOUNCE_MS = 200;

/** Place the caret at the end of the composer after draft hydration. */
export function focusEditorAtEnd(editor: LexicalEditor): void {
  // Node methods need an active editor state — `focus()`'s callback is NOT
  // an update context, and Lexical's dev build throws on the bare
  // `$getRoot()` (the prod build silently tolerated it). Set the selection
  // inside an update, then focus.
  editor.update(() => {
    $getRoot().selectEnd();
  });
  editor.focus();
}

export interface UseMessageDraftParams {
  flowSessionId?: string;
  canvasId?: string;
  enabled: boolean;
  onRestoreReferencedEntities?: (entities: ReferencedEntity[]) => void;
}

function collectConnectionReferencedEntities(
  nodes: LexicalNode[],
): ReferencedEntity[] {
  const entities: ReferencedEntity[] = [];

  const walk = (node: LexicalNode) => {
    if (node instanceof ConnectionNode) {
      const { platform, type, identifier, title } = node.getConnection();
      entities.push({
        entity_id: identifier || "",
        entity_type: type || "",
        name: title || "",
        platform: platform?.toLowerCase() || "",
      });
      return;
    }
    if ($isElementNode(node)) {
      for (const child of node.getChildren()) {
        walk(child);
      }
    }
  };

  for (const node of nodes) {
    walk(node);
  }

  return entities;
}

export function extractReferencedEntitiesFromEditor(
  editor: ReturnType<typeof useLexicalComposerContext>[0],
): ReferencedEntity[] {
  let entities: ReferencedEntity[] = [];
  editor.getEditorState().read(() => {
    entities = collectConnectionReferencedEntities($getRoot().getChildren());
  });
  return entities;
}

export function useMessageDraft({
  flowSessionId,
  canvasId,
  enabled,
  onRestoreReferencedEntities,
}: UseMessageDraftParams) {
  const [editor] = useLexicalComposerContext();
  const { identity } = useMessageDraftIdentity();
  const storageKey =
    identity && enabled
      ? resolveDraftStorageKey(identity, { flowSessionId, canvasId })
      : null;

  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const pendingWriteRef = useRef<{
    key: string;
    editorState: SerializedEditorState;
  } | null>(null);
  const hydratedKeyRef = useRef<string | null>(null);
  const skipNextSaveRef = useRef(false);

  const flushPendingWrite = useCallback(() => {
    clearTimeout(timerRef.current);
    if (!pendingWriteRef.current) return;
    const { key, editorState } = pendingWriteRef.current;
    pendingWriteRef.current = null;
    if (isEditorStateEmpty(editorState)) {
      removeMessageDraft(key);
    } else {
      writeMessageDraft(key, editorState);
    }
  }, []);

  const clearDraft = useCallback(() => {
    flushPendingWrite();
    if (storageKey) {
      removeMessageDraft(storageKey);
    }
  }, [flushPendingWrite, storageKey]);

  // Hydrate draft once per storage key.
  useEffect(() => {
    if (!storageKey || hydratedKeyRef.current === storageKey) return;

    const entry = readMessageDraft(storageKey);
    hydratedKeyRef.current = storageKey;

    const currentState = editor.getEditorState().toJSON();
    const editorHasContent = !isEditorStateEmpty(currentState);

    // Identity resolves asynchronously; the composer stays editable while
    // storageKey is null. Keep in-progress typing instead of clearing or
    // replacing it when hydration finally runs.
    if (editorHasContent) {
      writeMessageDraft(storageKey, currentState);
      onRestoreReferencedEntities?.(
        extractReferencedEntitiesFromEditor(editor),
      );
      return;
    }

    // A structurally-empty draft (stale format, bare empty paragraph) must
    // not hydrate: it fills the editor with invisible content, which hides
    // the placeholder while the composer LOOKS empty. Treat it as no draft
    // and heal the stored entry.
    if (!entry || isEditorStateEmpty(entry.editorState)) {
      if (entry) removeMessageDraft(storageKey);
      return;
    }

    skipNextSaveRef.current = true;
    try {
      const parsedState = editor.parseEditorState(
        JSON.stringify(entry.editorState),
      );
      editor.setEditorState(parsedState);
      const entities = extractReferencedEntitiesFromEditor(editor);
      onRestoreReferencedEntities?.(entities);
      // AutoFocusPlugin runs on the empty editor before hydration; move the
      // caret to the end of the restored draft so typing continues in-place.
      requestAnimationFrame(() => {
        focusEditorAtEnd(editor);
      });
    } catch {
      removeMessageDraft(storageKey);
      editor.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
      onRestoreReferencedEntities?.([]);
    }
  }, [editor, onRestoreReferencedEntities, storageKey]);

  // Reset hydration tracking when the storage key changes.
  useEffect(() => {
    if (!storageKey) {
      hydratedKeyRef.current = null;
    }
  }, [storageKey]);

  // Debounced save on editor updates.
  useEffect(() => {
    if (!storageKey) return;

    return editor.registerUpdateListener(({ editorState }) => {
      if (skipNextSaveRef.current) {
        skipNextSaveRef.current = false;
        return;
      }

      clearTimeout(timerRef.current);
      const serialized = editorState.toJSON();
      pendingWriteRef.current = { key: storageKey, editorState: serialized };
      timerRef.current = setTimeout(() => {
        flushPendingWrite();
      }, SAVE_DEBOUNCE_MS);
    });
  }, [editor, flushPendingWrite, storageKey]);

  // Flush pending write on unmount or key change.
  useEffect(() => {
    return () => {
      flushPendingWrite();
    };
  }, [flushPendingWrite, storageKey]);

  return { clearDraft };
}
